import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { styled } from '@cp/ds/src/theme';
import { FormField } from '@cp/ds/src/components/form';
import { Box, BoxProps } from '@cp/ds/src/components/box';
import { Button, ButtonProps } from '@cp/ds/src/components/button';
import { FormProvider, SubmitHandler, UseFormReturn, useForm } from 'react-hook-form';
import { SerializedError } from '@reduxjs/toolkit';
import { useApiServerErrorHandler } from 'src/shared/apiServer';
import { PromoCodeFormModel, promoFormResolver } from '../model';

const Root = styled(Box, { name: 'PromoForm' })(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

export type ActivatePromocodeFormRef = UseFormReturn<PromoCodeFormModel>;
export interface ActivatePromoCodeFormProps extends BoxProps {
  size?: 'small' | 'normal';
  error?: Error | SerializedError;
  SubmitButton?: (props: ButtonProps) => JSX.Element | null;
  submitProps?: Partial<ButtonProps>;
  onValid?: SubmitHandler<PromoCodeFormModel>;
}

export const ActivatePromocodeForm = forwardRef<ActivatePromocodeFormRef, ActivatePromoCodeFormProps>(
  (
    {
      size,
      error,
      onValid = () => console.warn('PromoForm has no onValid nor onSubmit handlers'),
      SubmitButton = Button,
      submitProps,
      ...props
    },
    ref,
  ) => {
    const form = useForm<PromoCodeFormModel>({
      mode: 'onSubmit',
      reValidateMode: 'onSubmit',
      resolver: promoFormResolver,
    });
    const handleApiServerError = useApiServerErrorHandler();

    useEffect(() => {
      if (!error) {
        return;
      }
      handleApiServerError(error, { form, formFieldNameForCommonErrors: 'id' });
    }, [error, form, handleApiServerError]);

    useImperativeHandle(ref, () => form, [form]);

    return (
      <FormProvider {...form}>
        <Root component="form" noValidate onSubmit={form.handleSubmit(onValid)} {...props}>
          <FormField
            autoComplete="off"
            fieldContainerProps={{ sx: { flex: '1 1 0' } }}
            name="id"
            placeholder="Введите промокод"
            size={size}
            type="text"
          />
          <SubmitButton color="pink" size={size} sx={{ py: 0 }} type="submit" {...submitProps}>
            Активировать
          </SubmitButton>
        </Root>
      </FormProvider>
    );
  },
);
